/**
 * The 1:1s contract — one flat router, `oneOnOneContract` (`/one-on-ones`), covering meetings,
 * agenda items, and action items. See the Feedback & 1:1s plan's "Contract & API" section.
 *
 * NO WORKFLOW ENGINE ANYWHERE IN THIS FILE. Scheduling a 1:1 is not an approval and an action item is
 * not either — `@hrms/workflow` is never imported by routes/one-on-one.ts. State that explicitly
 * there too, per the plan, so a future reviewer does not "fix" a missing approval step that was never
 * supposed to exist.
 *
 * `createMeeting` TAKES ONE `participantPersonId`, NOT SEPARATE MANAGER/EMPLOYEE FIELDS. Either side
 * of a reporting relationship can initiate — "a manager schedules with a direct report" and "an
 * employee requests a 1:1 with their own manager" are both real flows in the plan's Verification
 * section — so the route resolves which of the two the caller is from the org chart rather than the
 * client asserting it, which would let a caller lie about their own role in the meeting.
 *
 * `listActionItems` IS THE CROSS-MEETING INBOX, DELIBERATELY SEPARATE FROM `listAgendaItems`. Agenda
 * items are only ever read per-meeting; action items are read both per-meeting and as "everything
 * assigned to me" — see `ownerPersonId`/`status` on the query and `OneOnOneActionItem`'s
 * `[orgId, ownerPersonId, status]` index in schema.prisma.
 *
 * OWNERSHIP AND LIFECYCLE (performance remediation #26, ADR 0031, plan §5K/§6):
 *   - An agenda item's title/order/goal is edited or deleted only by the person who added it
 *     (`addedByPersonId`) — 403 for the other party. Either party may still tick `isDiscussed`.
 *   - Verifying an action item is `verifyActionItem`, never a client-supplied id: only the OTHER party
 *     of the item's meeting (never its owner) may verify, and the server records the caller as the
 *     verifier. The item must be `done` (409 otherwise).
 *   - A meeting leaves `scheduled` through `completeMeeting` / `cancelMeeting` / `markMeetingNoShow`
 *     and every outcome is terminal (decision Q9: a completed meeting is never reopened — a missed
 *     one is rescheduled as a new meeting). Completing a recurring meeting opens the next occurrence
 *     exactly once, however often the call is retried.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { ACTION_ITEM_STATUSES, ONE_ON_ONE_STATUSES, ONE_ON_ONE_RECURRENCES } from '../enums.js';
import {
  dateOnlySchema,
  errorBodySchema,
  instantSchema,
  orgIdSchema,
  paginatedSchema,
  paginationQuerySchema,
  timezoneSchema,
  uuidSchema,
} from '../schemas/common.js';
import { routeMeta } from './meta.js';

const c = initContract();

// ---------------------------------------------------------------------------
// Shared enum schemas
// ---------------------------------------------------------------------------

export const oneOnOneStatusSchema = z.enum(ONE_ON_ONE_STATUSES);
export const oneOnOneRecurrenceSchema = z.enum(ONE_ON_ONE_RECURRENCES);
export const actionItemStatusSchema = z.enum(ACTION_ITEM_STATUSES);

// ---------------------------------------------------------------------------
// DTOs
// ---------------------------------------------------------------------------

/**
 * `timezone` is stored on the row, not resolved from `location.timezone` at render time (CLAUDE.md
 * invariant 3's "Thu 5:30 PM" case) — a location transfer after scheduling must not silently move a
 * meeting's displayed time. No manager-private-notes field — see schema.prisma's `OneOnOneMeeting`
 * doc comment for why that is its own table if ever wanted, never a column here.
 */
export const oneOnOneMeetingSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  managerPersonId: uuidSchema,
  managerName: z.string(),
  employeePersonId: uuidSchema,
  employeeName: z.string(),
  scheduledAt: instantSchema,
  durationMinutes: z.number().int().positive(),
  timezone: timezoneSchema,
  status: oneOnOneStatusSchema,
  meetingUrl: z.string().url().nullable(),
  /** Column kept, button cut — see schema.prisma: no calendar integration exists yet. */
  externalCalendarRef: z.string().nullable(),
  reviewId: uuidSchema.nullable(),
  recurrence: oneOnOneRecurrenceSchema,
  createdAt: instantSchema,
});
export type OneOnOneMeeting = z.infer<typeof oneOnOneMeetingSchema>;

export const agendaItemSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  meetingId: uuidSchema,
  title: z.string(),
  addedByPersonId: uuidSchema,
  addedByName: z.string(),
  goalId: uuidSchema.nullable(),
  isDiscussed: z.boolean(),
  sortOrder: z.number().int(),
});
export type AgendaItem = z.infer<typeof agendaItemSchema>;

export const actionItemSchema = z.object({
  id: uuidSchema,
  orgId: orgIdSchema,
  meetingId: uuidSchema.nullable(),
  ownerPersonId: uuidSchema,
  ownerName: z.string(),
  title: z.string(),
  dueOn: dateOnlySchema.nullable(),
  status: actionItemStatusSchema,
  verifiedByPersonId: uuidSchema.nullable(),
  goalId: uuidSchema.nullable(),
  createdAt: instantSchema,
});
export type ActionItem = z.infer<typeof actionItemSchema>;

// ---------------------------------------------------------------------------
// Requests — meetings
// ---------------------------------------------------------------------------

export const listMeetingsQuerySchema = paginationQuerySchema.extend({
  /** Either side of the pair. Defaults to the caller. */
  personId: uuidSchema.optional(),
  status: oneOnOneStatusSchema.optional(),
});

export const createMeetingBodySchema = z.object({
  /** The other side of the pair — see this file's header for why there is one field, not two. */
  participantPersonId: uuidSchema,
  scheduledAt: instantSchema,
  durationMinutes: z.number().int().min(5).max(480).default(30),
  timezone: timezoneSchema,
  meetingUrl: z.string().url().optional(),
  reviewId: uuidSchema.optional(),
  /** PRF-15. Defaults to 'none' (a one-off meeting) when omitted. */
  recurrence: oneOnOneRecurrenceSchema.optional(),
});

export const updateMeetingBodySchema = z.object({
  scheduledAt: instantSchema.optional(),
  durationMinutes: z.number().int().min(5).max(480).optional(),
  timezone: timezoneSchema.optional(),
  /**
   * @deprecated Use the explicit transition routes (`completeMeeting`, `cancelMeeting`,
   * `markMeetingNoShow`); the server applies the same lifecycle here and rejects illegal transitions
   * (409) — including any move out of `completed`/`cancelled`/`no_show` (no reopen, decision Q9).
   * Still accepted so installed mobile builds keep working (performance remediation R5); removed
   * after the next mobile release.
   */
  status: oneOnOneStatusSchema
    .optional()
    .describe(
      'deprecated: use the explicit transition routes; the server rejects illegal transitions',
    ),
  meetingUrl: z.string().url().optional(),
  /** PRF-15. Set to 'none' to stop a series; setting it alongside `status: 'completed'` in the
   *  same call uses the NEW value when deciding whether to auto-create the next occurrence. Only a
   *  `scheduled` meeting may be edited at all — a finished one is history (409). */
  recurrence: oneOnOneRecurrenceSchema.optional(),
});

/** The explicit transitions take no body: the outcome is the route, the actor is the caller. */
export const meetingTransitionBodySchema = z.object({});

// ---------------------------------------------------------------------------
// Requests — agenda items
// ---------------------------------------------------------------------------

export const listAgendaItemsQuerySchema = paginationQuerySchema;

export const createAgendaItemBodySchema = z.object({
  title: z.string().trim().min(1).max(200),
  goalId: uuidSchema.optional(),
});

/** `title`/`sortOrder`/`goalId` are the adder's alone (403 for the other party); `isDiscussed` may
 *  be ticked by either party. */
export const updateAgendaItemBodySchema = z.object({
  title: z.string().trim().min(1).max(200).optional(),
  isDiscussed: z.boolean().optional(),
  sortOrder: z.number().int().optional(),
  goalId: uuidSchema.optional(),
});

// ---------------------------------------------------------------------------
// Requests — action items
// ---------------------------------------------------------------------------

export const listActionItemsQuerySchema = paginationQuerySchema.extend({
  /** Defaults to the caller — the cross-meeting inbox read. */
  ownerPersonId: uuidSchema.optional(),
  meetingId: uuidSchema.optional(),
  status: actionItemStatusSchema.optional(),
});

export const createActionItemBodySchema = z.object({
  meetingId: uuidSchema.optional(),
  /** Defaults to the caller; a manager may assign to the report they scheduled the meeting with. */
  ownerPersonId: uuidSchema.optional(),
  title: z.string().trim().min(1).max(200),
  dueOn: dateOnlySchema.optional(),
  goalId: uuidSchema.optional(),
});

export const updateActionItemBodySchema = z.object({
  title: z.string().trim().min(1).max(200).optional(),
  dueOn: dateOnlySchema.optional(),
  /** Moving an item out of `done` clears its verification — an undone item is not verified. */
  status: actionItemStatusSchema.optional(),
  /**
   * @deprecated Use `verifyActionItem`. The verifier is always the caller, set by the server. Still
   * accepted so installed mobile builds keep working (performance remediation R5), but only when it
   * equals the caller AND the caller may verify this item (the other party of its meeting, item
   * `done` after this update) — anything else is 422. Removed after the next mobile release.
   */
  verifiedByPersonId: uuidSchema
    .optional()
    .describe(
      'deprecated: use verifyActionItem; must equal the caller, who must be allowed to verify',
    ),
  goalId: uuidSchema.optional(),
});

/** No verifier id in the body: the verifier is the caller, set by the server. */
export const verifyActionItemBodySchema = z.object({});

// ---------------------------------------------------------------------------
// Contract
// ---------------------------------------------------------------------------

export const oneOnOneContract = c.router(
  {
    listMeetings: {
      method: 'GET',
      path: '/',
      query: listMeetingsQuerySchema,
      responses: {
        200: paginatedSchema(oneOnOneMeetingSchema),
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "List 1:1 meetings in the caller's visible scope",
      metadata: routeMeta(null),
    },

    createMeeting: {
      method: 'POST',
      path: '/',
      body: createMeetingBodySchema,
      responses: {
        201: oneOnOneMeetingSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        "Schedule a 1:1 — either the manager or the employee side of the pair may initiate. No approval workflow: see this file's header.",
      metadata: routeMeta('one_on_one:schedule'),
    },

    updateMeeting: {
      method: 'PATCH',
      path: '/:meetingId',
      body: updateMeetingBodySchema,
      responses: {
        200: oneOnOneMeetingSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        'Reschedule a scheduled 1:1 or change its recurrence. The `status` field is deprecated — use completeMeeting/cancelMeeting/markMeetingNoShow; a finished meeting is never reopened (409)',
      metadata: routeMeta(null),
    },

    completeMeeting: {
      method: 'POST',
      path: '/:meetingId/complete',
      body: meetingTransitionBodySchema,
      responses: {
        200: oneOnOneMeetingSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary:
        'Mark a scheduled 1:1 completed (terminal). A recurring meeting opens its next occurrence exactly once, carrying open agenda/action items forward. Requires Idempotency-Key',
      metadata: routeMeta(['one_on_one:read', 'one_on_one:schedule']),
    },

    cancelMeeting: {
      method: 'POST',
      path: '/:meetingId/cancel',
      body: meetingTransitionBodySchema,
      responses: {
        200: oneOnOneMeetingSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'Cancel a scheduled 1:1 (terminal). Requires Idempotency-Key',
      metadata: routeMeta(['one_on_one:read', 'one_on_one:schedule']),
    },

    markMeetingNoShow: {
      method: 'POST',
      path: '/:meetingId/no-show',
      body: meetingTransitionBodySchema,
      responses: {
        200: oneOnOneMeetingSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary:
        'Record that a scheduled 1:1 did not happen (terminal — reschedule as a new meeting). Requires Idempotency-Key',
      metadata: routeMeta(['one_on_one:read', 'one_on_one:schedule']),
    },

    listAgendaItems: {
      method: 'GET',
      path: '/:meetingId/agenda-items',
      query: listAgendaItemsQuerySchema,
      responses: {
        200: paginatedSchema(agendaItemSchema),
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "One meeting's agenda, in sortOrder",
      metadata: routeMeta(null),
    },

    createAgendaItem: {
      method: 'POST',
      path: '/:meetingId/agenda-items',
      body: createAgendaItemBodySchema,
      responses: {
        201: agendaItemSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Add a topic to a 1:1',
      metadata: routeMeta(null),
    },

    updateAgendaItem: {
      method: 'PATCH',
      path: '/:meetingId/agenda-items/:agendaItemId',
      body: updateAgendaItemBodySchema,
      responses: {
        200: agendaItemSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary:
        'Edit an agenda item (its adder only — 403 otherwise), or mark it discussed (either party)',
      metadata: routeMeta(null),
    },

    deleteAgendaItem: {
      method: 'DELETE',
      path: '/:meetingId/agenda-items/:agendaItemId',
      body: z.object({}),
      responses: {
        200: z.object({ id: uuidSchema }),
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Remove an agenda item — its adder only (403 for the other party)',
      metadata: routeMeta(null),
    },

    listActionItems: {
      method: 'GET',
      path: '/action-items',
      query: listActionItemsQuerySchema,
      responses: {
        200: paginatedSchema(actionItemSchema),
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: "The action-item inbox across meetings — owner's open items by default",
      metadata: routeMeta(null),
    },

    // GET '/:meetingId' MUST be registered after every static single-segment GET path above ('/',
    // '/action-items') — same route-ordering convention as feedback.contract.ts's `getFeedback`; see
    // that file's comment for the full reasoning. '/:meetingId/agenda-items' (two segments) never
    // conflicts, so it stays above regardless.
    getMeeting: {
      method: 'GET',
      path: '/:meetingId',
      responses: {
        200: oneOnOneMeetingSchema,
        401: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Get one 1:1 meeting',
      metadata: routeMeta(null),
    },

    createActionItem: {
      method: 'POST',
      path: '/action-items',
      body: createActionItemBodySchema,
      responses: {
        201: actionItemSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
      },
      summary: 'Add an action item, optionally tied to a meeting',
      metadata: routeMeta(['one_on_one:read', 'one_on_one:schedule']),
    },

    updateActionItem: {
      method: 'PATCH',
      path: '/action-items/:actionItemId',
      body: updateActionItemBodySchema,
      responses: {
        200: actionItemSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        422: errorBodySchema,
      },
      summary:
        'Update or complete an action item. `verifiedByPersonId` is deprecated (use verifyActionItem): accepted only when it is the caller and the caller may verify, else 422',
      metadata: routeMeta(['one_on_one:read', 'one_on_one:schedule']),
    },

    verifyActionItem: {
      method: 'POST',
      path: '/action-items/:actionItemId/verify',
      body: verifyActionItemBodySchema,
      responses: {
        200: actionItemSchema,
        401: errorBodySchema,
        403: errorBodySchema,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary:
        "Verify a done action item — only the other party of the item's 1:1 (never its owner); the verifier is the caller, server-set. Requires Idempotency-Key",
      metadata: routeMeta('one_on_one:schedule'),
    },
  },
  { pathPrefix: '/one-on-ones' },
);
